import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Trophy, Check, Timer, Zap, Medal, Sparkles, Cake, Flag, Car, Utensils, Hand, Gauge, Rocket, TrendingUp } from 'lucide-react';
import { useSiteSeo } from '@/lib/useSiteSeo';
import { usePageMedia } from '@/lib/usePageMedia';
import GroupEnquiryForm from '@/components/groupbookings/GroupEnquiryForm';
import PartyDepositModal from '@/components/groupbookings/PartyDepositModal';

const PACKAGE_TIERS = [
  {
    key: 'junior_grand_prix',
    icon: Trophy,
    title: 'JUNIOR GRAND PRIX',
    subtitle: 'The Ultimate Junior Racing Party',
    tag: 'AGES 8–15',
    price: 30,
    duration: '1.5 HOURS',
    groupSize: '4–8 DRIVERS',
    durationSubtitle: '1.5 Hours • 4–8 Drivers',
    desc: 'Experience the excitement of a real Grand Prix weekend! Perfect for birthdays and celebrations, the Junior Grand Prix puts young drivers at the heart of the action. They\'ll practice, qualify for their grid position, and compete in an exciting race before celebrating on the podium just like the professionals.\n\nWhether they\'re racing for the win or simply enjoying the experience with friends, every driver leaves with great memories, a certificate, and plenty of photos.',
    features: [
      'Dedicated Race Director & Host',
      'Driver safety briefing & simulator setup',
      'Practice and qualifying session',
      'Snack and drink break (Drivers rotate / relax)',
      'Grand Prix grid race',
      'Podium celebration & photos',
      'Official driver certificates for all participants',
    ],
    format: [
      { icon: Flag, time: '15 Mins', label: 'Safety briefing and simulator setup' },
      { icon: Car, time: '20 Mins', label: 'Practice and qualifying session' },
      { icon: Utensils, time: '20 Mins', label: 'Snack and drink break (Driver rotation & downtime)' },
      { icon: Car, time: '20 Mins', label: 'Grand Prix grid race' },
      { icon: Trophy, time: '10 Mins', label: 'Podium celebration, certificates and photos' },
    ],
    awards: {
      headline: 'Who will take pole position, who will conquer the race, and who will stand on the top step of the podium?',
      intro: 'Every driver takes home an official certificate, featuring special award categories:',
      items: [
        { icon: Trophy, label: 'Fastest Lap Award' },
        { icon: Zap, label: 'Pole Position Award' },
        { icon: Rocket, label: 'Overtake of the Day' },
        { icon: Sparkles, label: 'Driver of the Day' },
        { icon: Medal, label: 'Best Sportsmanship' },
        { icon: TrendingUp, label: 'Most Improved Driver' },
        { icon: Cake, label: 'Birthday Driver Award' },
        { icon: Flag, label: 'Future Racing Star' },
      ],
    },
    cta: 'BOOK NOW',
    deposit: true,
  },
  {
    key: 'ultimate_endurance',
    icon: Users,
    title: 'ULTIMATE ENDURANCE EXPERIENCE',
    subtitle: 'The Ultimate Team Racing Party',
    tag: 'AGES 8–15',
    price: 50,
    duration: '2 HOURS',
    groupSize: '4–8 DRIVERS',
    durationSubtitle: '2.0 Hours • 4–8 Drivers',
    desc: 'Take your race day to the next level with our longest and most competitive party experience. Work together in an exciting endurance race where strategy, consistency and teamwork are just as important as outright speed. Then flip the grid for an action-packed reverse-grid sprint race, giving everyone a chance to fight for victory and a place on the podium.',
    features: [
      'Dedicated Race Director & Event Host',
      'Driver Safety Briefing & Simulator Setup',
      'Team Endurance Race',
      'Dedicated Break for Cake, Food & Snacks',
      'Reverse-Grid Sprint Race',
      'Live Telemetry Timing & Race Results',
      'Official Trophy & Medal Presentation',
      'Podium Celebration & Photos',
    ],
    format: [
      { icon: Flag, time: '15 Mins', label: 'Driver briefing and simulator setup' },
      { icon: Car, time: '35 Mins', label: 'Endurance Team Championship' },
      { icon: Utensils, time: '25 Mins', label: 'Food, snacks and birthday cake break' },
      { icon: Zap, time: '35 Mins', label: 'Reverse-Grid Sprint Race' },
      { icon: Trophy, time: '10 Mins', label: 'Podium ceremony, trophies, medals and photos' },
    ],
    awards: {
      headline: 'Who will conquer the distance, lead their team to victory, and stand tallest on the podium?',
      intro: 'Every driver takes home an official certificate, featuring special award categories:',
      items: [
        { icon: Trophy, label: 'Ultimate Endurance Champion' },
        { icon: Zap, label: 'Fastest Lap Award' },
        { icon: Rocket, label: 'Overtake of the Day' },
        { icon: Sparkles, label: 'Driver of the Day' },
        { icon: Medal, label: 'Teamwork Award' },
        { icon: TrendingUp, label: 'Most Improved Driver' },
        { icon: Cake, label: 'Birthday Driver Award' },
        { icon: Flag, label: 'Endurance Finisher Award' },
      ],
    },
    cta: 'BOOK NOW',
    deposit: true,
  },
];

const PARTY_ADDONS = [
  {
    icon: Utensils,
    title: 'Catering & Pizza Pack',
    subtitle: 'Fresh hot pizzas, juice, and snacks served during your scheduled celebration break.',
    price: 'From £6 / driver',
  },
  {
    icon: Trophy,
    title: 'Engraved Podium Trophy Set',
    subtitle: 'Award your top 3 finishers authentic custom engraved motorsport trophies to keep.',
    price: '£15 per set',
  },
  {
    icon: Hand,
    title: 'Motion Reality Racing Gloves',
    subtitle: 'Official branded sim racing grip gloves for all drivers to keep as souvenirs.',
    price: '£8 / driver',
  },
  {
    icon: Gauge,
    title: 'Parent & Guest Rig Add-On',
    subtitle: 'Add 2 motion rigs so parents or guests can join the racing action during the session.',
    price: 'From £25 / rig',
  },
];

export default function Parties() {
  const [depositModalOpen, setDepositModalOpen] = React.useState(false);
  const [depositPackage, setDepositPackage] = React.useState(null);
  const formRef = React.useRef(null);
  const { get: getPageMedia } = usePageMedia();

  useSiteSeo('parties', {
    title: 'Parties & Groups | The Ultimate Sim Racing Party | Motion Reality',
    description: 'The ultimate sim racing party at Motion Reality. Junior racers (ages 8–15), adult birthdays, stag & hen dos, and custom group hire. Book now or enquire for availability.',
  });

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-[#0d0d0f] min-h-screen">
      {/* ── HERO ── */}
      <section className="relative min-h-[40vh] flex items-center overflow-hidden">
        <img
          src={getPageMedia('parties', 'hero')}
          alt="Group sim racing party at Motion Reality"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/20 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-primary text-xs font-bold tracking-[0.3em] mb-3">PARTIES & GROUPS</p>
            <h1 className="text-white font-black uppercase leading-[0.9] mb-5" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>
              The Ultimate<br />Sim Racing Party
            </h1>
            <p className="text-white/60 text-sm md:text-base max-w-xl mb-8 leading-relaxed">
              From junior birthday bashes to stag & hen weekends, we host high-energy group racing experiences that everyone will be talking about for weeks.
            </p>
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest px-8 py-3.5 transition-colors"
            >
              CHECK AVAILABILITY <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── PACKAGE TIERS ── */}
      <section className="py-16 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 text-center">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">CHOOSE YOUR PACKAGE</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight">Party Packages</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {PACKAGE_TIERS.map((pkg, i) => {
              const Icon = pkg.icon;
              return (
                <motion.div
                  key={pkg.key}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative border bg-[#111115] p-6 h-full flex flex-col hover:border-primary/30 transition-colors group ${
                    pkg.deposit ? 'border-primary/30' : 'border-white/5'
                  }`}
                >
                  {pkg.deposit && (
                    <span className="absolute -top-3 left-6 bg-primary text-white text-[9px] font-black tracking-widest px-3 py-1 z-10">
                      INSTANT BOOK
                    </span>
                  )}

                  <div className="flex flex-col gap-5 flex-1">
                    {/* 1. Header */}
                    <div>
                      <div className="w-12 h-12 flex items-center justify-center bg-primary/10 border border-primary/20 mb-4 group-hover:bg-primary/20 transition-colors">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <p className="text-primary text-[10px] font-bold tracking-widest mb-1">{pkg.tag}</p>
                      <h3 className="text-white font-black text-lg tracking-wide mb-1">{pkg.title}</h3>
                      {pkg.subtitle && <p className="text-white/50 text-sm font-medium">{pkg.subtitle}</p>}
                    </div>

                    {/* Description */}
                    <div className="text-white/40 text-xs leading-relaxed space-y-2 min-h-[8.5rem]">
                      {pkg.desc.split('\n\n').map((para, idx) => (
                        <p key={idx}>{para}</p>
                      ))}
                    </div>

                    {/* 2. Headline Pricing */}
                    <div className="py-3 border-y border-white/5">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black text-white">£{pkg.price}</span>
                        <span className="text-sm font-medium text-white/50">/ driver</span>
                      </div>
                      <p className="text-sm text-neutral-400 mt-1">{pkg.durationSubtitle}</p>
                    </div>

                    {/* 3. What's Included */}
                    <div className="min-h-[15rem]">
                      <p className="text-primary text-xs font-bold tracking-widest border-b border-white/5 pb-2 mb-3">WHAT'S INCLUDED</p>
                      <ul className="space-y-2 leading-relaxed">
                        {pkg.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-xs text-white/50">
                            <Check className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* 4. Race Day Format */}
                    {pkg.format && (
                      <div>
                        <p className="text-primary text-xs font-bold tracking-widest border-b border-white/5 pb-2 mb-3">RACE DAY FORMAT</p>
                        <ul className="space-y-2">
                          {pkg.format.map((step, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-xs">
                              <span className="shrink-0">{(() => { const StepIcon = step.icon; return <StepIcon className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />; })()}</span>
                              <span className="text-white/30 font-mono font-bold tabular-nums shrink-0 w-14">{step.time}</span>
                              <span className="text-white/50">{step.label}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* 5. Callout / Audience Pills */}
                    {pkg.callout && (
                      <div className="bg-primary/5 border-y border-primary/10 -mx-6 px-6 py-4">
                        <p className="text-white/70 text-xs font-medium italic leading-relaxed">{pkg.callout}</p>
                      </div>
                    )}
                    {pkg.awards && (
                      <div className="bg-primary/5 border-y border-primary/10 -mx-6 px-6 py-4 mt-auto">
                        <p className="text-white/60 text-xs italic leading-relaxed mb-2 min-h-[3.75rem]">"{pkg.awards.headline}"</p>
                        <p className="text-white/40 text-[11px] mb-3">{pkg.awards.intro}</p>
                        <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
                          {pkg.awards.items.map((award, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 text-xs text-white/60">
                              <span className="shrink-0 w-6 h-6 flex items-center justify-center bg-white/5 border border-white/10 rounded-full">{(() => { const AwardIcon = award.icon; return <AwardIcon className="w-3 h-3 text-primary/70" />; })()}</span>
                              <span className="font-medium">{award.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 6. Pinned BOOK NOW Button */}
                  {pkg.deposit ? (
                    <button
                      onClick={() => { setDepositPackage(pkg); setDepositModalOpen(true); }}
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest py-3 transition-colors"
                    >
                      {pkg.cta} <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={scrollToForm}
                      className="mt-6 w-full inline-flex items-center justify-center gap-2 border border-white/10 hover:border-primary/50 text-white/70 hover:text-primary font-bold text-xs tracking-widest py-3 transition-colors"
                    >
                      {pkg.cta} <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── OPTIONAL EXTRAS & ADD-ONS ── */}
      <section className="py-16 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 text-center">
            <p className="text-primary text-xs font-bold uppercase tracking-wider mb-2">UPGRADE YOUR RACE DAY</p>
            <h2 className="text-white font-bold text-2xl mb-2">Optional Party Add-Ons</h2>
            <p className="text-white/40 text-sm">Customise your celebration with food, podium awards, and keepsakes.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PARTY_ADDONS.map((addon, i) => (
              <motion.div
                key={addon.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#121216] border border-white/10 rounded-xl p-5 hover:border-primary/50 transition-all flex flex-col h-full"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 rounded-lg mb-3">{(() => { const AddonIcon = addon.icon; return <AddonIcon className="w-5 h-5 text-primary" />; })()}</div>
                <h3 className="text-white font-bold text-sm mb-2 leading-tight">{addon.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed mb-4 flex-1">{addon.subtitle}</p>
                <p className="text-primary text-xs font-bold tracking-wide">{addon.price}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-white/30 text-xs text-center mt-6">
            Add-ons can be selected during checkout or confirmed up to 48 hours before your booking.
          </p>
        </div>
      </section>

      {/* ── LEAD FORM ── */}
      <section ref={formRef} className="py-16 px-6 lg:px-10 bg-[#111115] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 text-center">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">GET STARTED</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight mb-3">Check Availability</h2>
            <p className="text-white/40 text-sm max-w-xl mx-auto leading-relaxed">
              Tell us about your party and we'll check availability and put together a tailored package. Our team responds within 24 hours.
            </p>
          </div>
          <GroupEnquiryForm type="party" />
        </div>
      </section>

      {/* ── DEPOSIT MODAL ── */}
      <PartyDepositModal
        open={depositModalOpen}
        onClose={() => { setDepositModalOpen(false); setDepositPackage(null); }}
        packageTier={depositPackage?.key}
        packageName={depositPackage?.title}
        packagePrice={depositPackage?.price}
        onEnquiryScroll={scrollToForm}
      />
    </div>
  );
}